type RevealResult = { status: 'found'; target: HTMLElement } | { status: 'waiting' | 'missing' | 'unquoted' };

const quotedPassage = /[“"「『]([^”"」』]{2,300})[”"」』]/gu;

export function reviewChineseQuotes(message: string): string[] {
    const findingMarker = 'automatic check found';
    const findingOffset = message.toLowerCase().indexOf(findingMarker);
    const findingText = findingOffset < 0 ? message : message.slice(findingOffset + findingMarker.length);
    return [...findingText.matchAll(quotedPassage)]
        .map(match => match[1].trim().replace(/\.{3}$/, '').trim())
        .filter(quote => quote.length >= 2 && /\p{Script=Han}/u.test(quote));
}

function matchPosition(value: string, quote: string): { start: number; end: number } | null {
    const start = value.indexOf(quote);
    if (start >= 0) return { start, end: start + quote.length };
    // Generated findings may insert whitespace around punctuation or characters.
    const compact = (text: string) => text.replace(/\s+/gu, '');
    return compact(value).includes(compact(quote)) ? { start: 0, end: value.length } : null;
}

function openClosedDetails(element: HTMLElement, root: HTMLElement): boolean {
    let parent = element.closest<HTMLDetailsElement>('details:not([open])');
    let opened = false;
    while (parent && root.contains(parent)) {
        parent.open = true;
        opened = true;
        parent = parent.parentElement?.closest<HTMLDetailsElement>('details:not([open])') ?? null;
    }
    return opened;
}

export function revealExerciseReviewText(root: HTMLElement, message: string): RevealResult {
    const quotes = reviewChineseQuotes(message);
    if (!quotes.length) return { status: 'unquoted' };

    const activityToggle = root.querySelector<HTMLButtonElement>('button[aria-label$=" details"][aria-expanded="false"]');
    if (activityToggle) {
        activityToggle.click();
        return { status: 'waiting' };
    }

    const fields = [...root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input:not([type="hidden"]), textarea')];
    for (const quote of quotes) {
        for (const field of fields) {
            const match = matchPosition(field.value, quote);
            if (!match) continue;
            if (openClosedDetails(field, root)) return { status: 'waiting' };
            if (!field.getClientRects().length) continue;
            field.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
            // Move the field's internal viewport to the quote, then return typing
            // focus to the review window. Leaving a selected editable quote here
            // lets Enter or Space meant for review navigation overwrite it.
            field.focus({ preventScroll: true });
            try { field.setSelectionRange(match.start, match.start); } catch { /* Non-text inputs do not support selection. */ }
            const review = document.querySelector<HTMLElement>('[aria-label="Lesson review navigator"]');
            const next = review?.querySelector<HTMLButtonElement>('button[aria-label="Next review item"]:not(:disabled)');
            (next ?? review ?? root).focus({ preventScroll: true });
            return { status: 'found', target: field.closest<HTMLElement>('.MuiFormControl-root') ?? field };
        }
        const sentenceRows = [...root.querySelectorAll<HTMLElement>('[data-exercise-edit-scope^="sentence:"]')];
        for (const sentenceRow of sentenceRows) {
            if (!matchPosition(sentenceRow.textContent ?? '', quote)) continue;
            if (openClosedDetails(sentenceRow, root)) return { status: 'waiting' };
            const edit = [...sentenceRow.querySelectorAll<HTMLButtonElement>('button')]
                .find(button => button.textContent?.trim() === 'Edit');
            if (edit) {
                edit.click();
                return { status: 'waiting' };
            }
        }
        const specializedEditors = [...root.querySelectorAll<HTMLElement>('[data-exercise-edit-scope^="specialized:"]')];
        for (const editor of specializedEditors) {
            if (!matchPosition(editor.textContent ?? '', quote)) continue;
            if (openClosedDetails(editor, root)) return { status: 'waiting' };
            const edit = [...editor.querySelectorAll<HTMLButtonElement>('button')]
                .find(button => /^Edit\s/u.test(button.textContent?.trim() ?? '') && !button.disabled);
            if (edit) {
                edit.click();
                return { status: 'waiting' };
            }
        }
    }
    return { status: 'missing' };
}
